import { History } from "../../compose/Icon";
import type { ResolvedCommentVersion } from "../../lib/commentVersionState";

/**
 * The `vX` version ribbon on a comment's quote (owner request 2026-07-28: "next
 * to a citation to an earlier version there must be a small vX label, ideally
 * looking like a ribbon to the right side of the quote").
 *
 * It sits at the RIGHT EDGE of the quote row and reads as a bookmark ribbon —
 * a small tag with a notched tail, tucked against the quote's edge.
 *
 * ── WHY IT IS TEXT AND NOT JUST A TINT ──────────────────────────────────────
 *
 * This ribbon is the REDUNDANT, non-colour signal that makes the card's three
 * states distinguishable without relying on colour or opacity (which fail
 * colour-blind and low-vision readers). `open-obsolete` is the state that would
 * otherwise be "the faded one", so it is precisely the state that must carry a
 * literal word. `v3` plus a history glyph is legible at any contrast and reads
 * correctly to a screen reader.
 *
 * ── LAYOUT CONTRACT (the two ways a ribbon like this normally breaks) ────────
 *
 * 1. It must never overlap or truncate the quote. It is a FLEX SIBLING of the
 *    quote text with `flex: 0 0 auto`, not an absolutely-positioned overlay, so
 *    the text box is laid out around it rather than under it.
 * 2. It must survive a long quote in a narrow pane. The quote text gets
 *    `min-width: 0` and wraps; the ribbon keeps its intrinsic width and stays
 *    pinned to the top-right, so it can neither be squeezed to nothing nor push
 *    the quote out of the card. (See `.comment-card__quote-row` in
 *    styles/annotations.css.)
 *
 * Presentational only — it renders no interaction. The click affordance belongs
 * to the quote button that contains it, so a reader cannot land on a control
 * that looks like a label.
 */
export function VersionRibbon({
  commentId,
  version,
  /** True when the note's version is EARLIER than what is on screen — the
   *  "obsolete" direction. Drives the ribbon's emphasis and its wording, so the
   *  label never claims staleness about a note that is simply from a version you
   *  are not currently reading. */
  behind,
}: {
  commentId: number;
  version: ResolvedCommentVersion;
  behind: boolean;
}) {
  const title = behind
    ? `This note was written against ${version.label}, an earlier version than the one shown. Click the quote to switch.`
    : `This note was written against ${version.label}, not the version shown. Click the quote to switch.`;
  return (
    <span
      className="version-ribbon"
      data-testid={`comment-version-ribbon-${commentId}`}
      data-version-id={version.versionId}
      data-version-seq={version.seq}
      data-behind={behind ? "" : undefined}
      title={title}
    >
      <History size={10} strokeWidth={2} aria-hidden="true" />
      {/* The visible label is terse (`v3`) because it lives in a tight ribbon;
          the accessible name spells out what it MEANS, since "v3" alone tells a
          screen-reader user nothing about why it is there. */}
      <span aria-hidden="true">{version.label}</span>
      <span className="sr-only">{title}</span>
    </span>
  );
}
